import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';

const FRUIT_EMOJIS = {
  Orange: '🍊', Lemon: '🍋', Grapefruit: '🍈', Mandarin: '🍊',
  Lime: '🍈', Tangerine: '🍊', Clementine: '🍊', Kumquat: '🟠'
};

export default function MyInspections() {
  const { t } = useLang();
  const { user } = useAuth();
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedId, setSelectedId] = useState(null);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);

  useEffect(() => { fetchInspections(); }, [page]);

  const deleteInspection = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm(t('delete_inspection_confirm'))) return;
    try {
      await api.delete(`/inspections/${id}`);
      fetchInspections();
    } catch (err) { console.error(err); }
  };

  const fetchInspections = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/inspections?page=${page}&limit=20`);
      setInspections(res.data.inspections);
      setTotalPages(res.data.totalPages);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const getMajorColor = (pct) => {
    const p = parseFloat(pct);
    if (p > 5) return 'bg-red-100 text-red-700 border-red-200';
    if (p > 2) return 'bg-orange-100 text-orange-700 border-orange-200';
    return 'bg-green-100 text-green-700 border-green-200';
  };

  return (
    <>
    <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #fff7ed 0%, #f8fafc 100%)' }}>
      <div className="max-w-6xl mx-auto px-4 py-6">
        <h1 className="text-2xl md:text-3xl font-extrabold text-gray-800 mb-6 fade-in">📋 {t('my_inspections')}</h1>

        {loading ? (
          <div className="text-center py-20">
            <div className="text-5xl fruit-spin inline-block">🍊</div>
            <p className="mt-4 text-orange-600 font-semibold">{t('loading')}</p>
          </div>
        ) : inspections.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-3xl shadow-lg">
            <div className="text-6xl mb-4">📦</div>
            <h2 className="text-xl font-bold text-gray-600">{t('no_inspections')}</h2>
            <p className="text-gray-400 mt-2">{t('no_inspections_desc')}</p>
          </div>
        ) : (
          <>
            <div className="grid gap-4">
              {inspections.map((insp, i) => (
                <div key={insp.id}
                  className="bg-white rounded-2xl shadow-md p-5 border border-orange-100 active:bg-orange-50 transition-all cursor-pointer fade-in touch-active"
                  style={{ animationDelay: `${i * 0.03}s` }}
                  onClick={() => setSelectedId(selectedId === insp.id ? null : insp.id)}>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center space-x-3">
                      <span className="text-3xl">{FRUIT_EMOJIS[insp.fruit_type] || '🍊'}</span>
                      <div>
                        <h3 className="font-bold text-gray-800">{insp.product_code} - {insp.fruit_type}</h3>
                        <p className="text-sm text-gray-500">
                          {t('size_code')} {insp.size_code} | {t('packager')}: <span className="font-semibold">{insp.packager_name || insp.packager_number}</span>{insp.packager_name && insp.packager_name !== insp.packager_number && <span className="text-gray-400"> ({insp.packager_number})</span>}
                        </p>
                        <p className="text-sm text-gray-500">
                          {t('inspector')}: <span className="font-semibold">{insp.inspector_name}</span>
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="text-center px-2">
                        <div className="text-xs text-yellow-600 font-bold">{t('minor_pct')}</div>
                        <div className="text-sm font-extrabold text-yellow-700">{parseFloat(insp.minor_defect_percent).toFixed(1)}%</div>
                      </div>
                      <div className={`px-3 py-2 rounded-xl text-sm font-bold border ${getMajorColor(insp.major_defect_percent)}`}>
                        <div className="text-xs">{t('major_pct')}</div>
                        <div>{parseFloat(insp.major_defect_percent).toFixed(1)}%</div>
                      </div>
                      {parseFloat(insp.critical_defect_percent) > 0 && (
                        <div className="px-3 py-2 rounded-xl text-sm font-bold border-2 bg-purple-100 text-purple-800 border-purple-400">
                          <div className="text-xs">💀 {t('critical_pct')}</div>
                          <div>{parseFloat(insp.critical_defect_percent).toFixed(1)}%</div>
                        </div>
                      )}
                      <div className="text-right text-xs text-gray-400 ml-2">
                        <div>{new Date(insp.created_at).toLocaleDateString()}</div>
                        <div>{new Date(insp.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                      </div>
                    </div>
                  </div>

                  {selectedId === insp.id && (
                    <div className="mt-4 pt-4 border-t border-orange-100 fade-in">
                      {insp.critical_defects > 0 && (
                        <div className="mb-3 p-3 bg-purple-900 text-white rounded-xl flex items-center gap-3">
                          <span className="text-3xl">💀</span>
                          <div>
                            <div className="font-extrabold">{t('critical_warning')}</div>
                            <div className="text-purple-200 text-sm">{insp.critical_defects} {t('critical_defects').toLowerCase()} ({parseFloat(insp.critical_defect_percent).toFixed(2)}%)</div>
                          </div>
                        </div>
                      )}
                      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 text-sm">
                        <div className="bg-orange-50 p-3 rounded-xl">
                          <div className="text-gray-500 text-xs">{t('total_pieces')}</div>
                          <div className="font-bold text-gray-800 text-lg">{insp.total_fruits}</div>
                        </div>
                        <div className="bg-yellow-50 p-3 rounded-xl">
                          <div className="text-gray-500 text-xs">{t('minor_defects')}</div>
                          <div className="font-bold text-yellow-700 text-lg">{insp.minor_defects} <span className="text-xs">({parseFloat(insp.minor_defect_percent).toFixed(2)}%)</span></div>
                        </div>
                        <div className="bg-red-50 p-3 rounded-xl">
                          <div className="text-gray-500 text-xs">{t('major_defects')}</div>
                          <div className="font-bold text-red-700 text-lg">{insp.major_defects} <span className="text-xs">({parseFloat(insp.major_defect_percent).toFixed(2)}%)</span></div>
                        </div>
                        <div className={`p-3 rounded-xl ${insp.critical_defects > 0 ? 'bg-purple-100 border-2 border-purple-300' : 'bg-purple-50'}`}>
                          <div className="text-gray-500 text-xs">💀 {t('critical_defects')}</div>
                          <div className={`font-bold text-lg ${insp.critical_defects > 0 ? 'text-purple-800' : 'text-gray-400'}`}>{insp.critical_defects} <span className="text-xs">({parseFloat(insp.critical_defect_percent).toFixed(2)}%)</span></div>
                        </div>
                        <div className="bg-blue-50 p-3 rounded-xl">
                          <div className="text-gray-500 text-xs">{t('total_defect')}</div>
                          <div className="font-bold text-gray-800 text-lg">{parseFloat(insp.total_defect_percent).toFixed(2)}%</div>
                        </div>
                        <div className="bg-green-50 p-3 rounded-xl">
                          <div className="text-gray-500 text-xs">{t('quality_class')}</div>
                          <div className="font-bold text-gray-800">{insp.quality_class}</div>
                        </div>
                      </div>
                      {insp.notes && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-xl text-sm text-gray-600">
                          <span className="font-semibold">{t('notes')}:</span> {insp.notes}
                        </div>
                      )}
                      {insp.photos && insp.photos.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {insp.photos.map((photo) => (
                            <img
                              key={photo.id}
                              src={photo.filepath}
                              alt="Inspection"
                              className="w-24 h-24 object-cover rounded-xl border-2 border-orange-200 cursor-pointer hover:border-orange-400 hover:opacity-90 transition-all"
                              onClick={(e) => { e.stopPropagation(); setLightboxPhoto(photo.filepath); }}
                            />
                          ))}
                        </div>
                      )}
                      <div className="mt-4 pt-3 border-t border-gray-100 flex justify-end">
                        <button
                          onClick={(e) => deleteInspection(insp.id, e)}
                          className="px-4 py-2 bg-red-50 text-red-600 rounded-xl text-sm font-semibold border border-red-200 active:bg-red-100 touch-active">
                          🗑️ {t('delete_inspection')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex justify-center items-center space-x-4 mt-8">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  className="px-6 py-3 rounded-xl bg-orange-100 text-orange-700 font-semibold disabled:opacity-30 active:bg-orange-200 transition-colors touch-active">
                  {t('previous')}
                </button>
                <span className="text-sm text-gray-600 font-medium">{page} {t('page_of')} {totalPages}</span>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                  className="px-6 py-3 rounded-xl bg-orange-100 text-orange-700 font-semibold disabled:opacity-30 active:bg-orange-200 transition-colors touch-active">
                  {t('next')}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>

    {/* Lightbox */}
    {lightboxPhoto && (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90 fade-in"
        onClick={() => setLightboxPhoto(null)}
      >
        <button
          className="absolute top-4 right-4 text-white text-3xl font-bold w-12 h-12 flex items-center justify-center rounded-full bg-black bg-opacity-50 hover:bg-opacity-80 transition-all"
          onClick={() => setLightboxPhoto(null)}
        >
          ×
        </button>
        <img
          src={lightboxPhoto}
          alt="Full size inspection"
          className="max-w-full max-h-full rounded-xl shadow-2xl"
          style={{ maxWidth: '95vw', maxHeight: '90vh', objectFit: 'contain' }}
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    )}
    </>
  );
}
